// 🔧 store_success.js
// ✅ Purpose: Public Store payment success page with order details
// ✅ Features:
// - Reads orderId from the success URL
// - Loads the Store order directly from /events/{eventId}/orders/{orderId}
// - Clears event-scoped Store cart from localStorage
// - Shows order items, totals, fulfillment method, and status
// - Uses orderId instead of Stripe session lookup, avoiding Firestore composite indexes
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
} from "firebase/firestore";
import { Link, useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { clearStoreCart } from "../../utils/store_cart_helpers";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";

const StoreSuccess = () => {

  const [searchParams] = useSearchParams();
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);

  const orderId = searchParams.get("orderId") || "";

  const centsToMoney = (cents) =>
    `$${(Number(cents || 0) / 100).toFixed(2)}`;

  useEffect(() => {
    if (!eventslug) return;
    clearStoreCart(eventslug);
  }, [eventslug]);

  useEffect(() => {
    const loadOrder = async () => {
      if (!eventId || !orderId) {
        setLoadingOrder(false);
        return;
      }

      setLoadingOrder(true);

      try {
        const orderSnap = await getDoc(
          doc(db, "events", eventId, "orders", orderId)
        );

        if (!orderSnap.exists()) {
          setOrder(null);
          return;
        }

        const orderData = {
          id: orderSnap.id,
          ...orderSnap.data(),
        };

        if (orderData.orderType !== "store") {
          setOrder(null);
          return;
        }

        setOrder(orderData);

        logger.log("✅ Store success order loaded", {
          eventId,
          orderId,
        });
      } catch (err) {
        logger.error("❌ Failed loading Store success order:", {
          value: err,
        });

        setOrder(null);
      } finally {
        setLoadingOrder(false);
      }
    };

    if (ready && eventId) {
      loadOrder();
    }
  }, [ready, eventId, orderId]);

  if (!ready || !eventId || eventLoading || loadingOrder) {
    return <FullPageSpinner message="Loading Store Confirmation..." />;
  }

  const cartItems = Array.isArray(order?.cartItems)
    ? order.cartItems
    : [];

return (
  <>
    <SeoHead
      title="Store Order Confirmation | Let's Go Vote"
      description="Store order confirmation."
      path={`/${eventslug}/store_success`}
      noindex={true}
    />

    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1000px" }}>
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body p-4 p-lg-5 text-center">
            <h1 className="display-5 fw-bold mb-3">
              ✅ Store Order Received
            </h1>

            <p className="lead text-muted">
              Thank you for your order from {eventDoc?.name || "the event"}.
            </p>

            <div className="alert alert-success text-start">
              Your payment was completed through Stripe. The event team will handle pickup or shipping based on your order details.
            </div>

            {orderId && (
              <div className="small text-muted">
                Order Reference: <code>{orderId}</code>
              </div>
            )}
          </div>
        </div>

        {order ? (
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
              <div>
                <strong>Order Details</strong>

                <div className="small text-muted">
                  {order.createdAt?.toDate
                    ? order.createdAt.toDate().toLocaleString()
                    : "Recently"}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <span className="badge bg-dark">
                  {order.status || "pending"}
                </span>

                <span className="badge bg-primary">
                  {order.fulfillmentMethod || "pickup"}
                </span>

                <span className="badge bg-secondary">
                  {order.fulfillmentStatus || "processing"}
                </span>
              </div>
            </div>

            <div className="card-body">
              <div className="row g-4">
                <div className="col-12 col-lg-7">
                  <h5 className="fw-bold mb-3">
                    Items
                  </h5>

                  {cartItems.length === 0 ? (
                    <div className="alert alert-info">
                      Item details are not available yet.
                    </div>
                  ) : (
                    <div className="list-group">
                      {cartItems.map((item, index) => (
                        <div
                          key={`${item.productId}-${item.variantId}-${index}`}
                          className="list-group-item"
                        >
                          <div className="d-flex justify-content-between gap-3">
                            <div>
                              <div className="fw-bold">
                                {item.productName || "Store Item"}
                              </div>

                              <div className="small text-muted">
                                {item.variantLabel || "Option"}
                              </div>

                              {item.sku && (
                                <div className="small text-muted">
                                  SKU: {item.sku}
                                </div>
                              )}
                            </div>

                            <div className="text-end">
                              <div className="small">
                                Qty {Number(item.quantity || 1)}
                              </div>

                              <div className="fw-bold">
                                {centsToMoney(
                                  Number(item.priceCents || 0) *
                                    Number(item.quantity || 1)
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-5">
                  <h5 className="fw-bold mb-3">
                    Summary
                  </h5>

                  <div className="border rounded bg-light p-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal</span>
                      <strong>{centsToMoney(order.subtotalCents || 0)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Shipping</span>
                      <strong>{centsToMoney(order.shippingCents || 0)}</strong>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                      <span>Total Paid</span>
                      <strong>
                        {centsToMoney(order.totalAmount || order.amountCents || 0)}
                      </strong>
                    </div>
                  </div>

                  <div className="border rounded bg-light p-3 mt-3 small">
                    <div>
                      <strong>Name:</strong>{" "}
                      {order.customerName || order.checkoutInfo?.customerName || "N/A"}
                    </div>

                    <div>
                      <strong>Email:</strong>{" "}
                      {order.buyerEmail || order.checkoutInfo?.email || "N/A"}
                    </div>

                    <div>
                      <strong>Fulfillment:</strong>{" "}
                      {order.fulfillmentMethod || "pickup"}
                    </div>

                    {order.fulfillmentMethod === "shipping" && (
                      <>
                        <hr />

                        <div className="fw-bold mb-1">
                          Shipping Address
                        </div>

                        <div>
                          {order.checkoutInfo?.shippingName ||
                            order.customerName ||
                            ""}
                        </div>

                        <div>{order.checkoutInfo?.shippingAddress1 || ""}</div>
                        <div>{order.checkoutInfo?.shippingAddress2 || ""}</div>

                        <div>
                          {order.checkoutInfo?.shippingCity || ""},{" "}
                          {order.checkoutInfo?.shippingState || ""}{" "}
                          {order.checkoutInfo?.shippingPostalCode || ""}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="alert alert-info">
            Your order was received. Detailed order information is not available on this page yet.
          </div>
        )}

        <div className="d-flex flex-wrap justify-content-center gap-2">
          <Link
            to={`/${eventslug}/store`}
            className="btn btn-outline-primary"
          >
            Back to Store
          </Link>

          <Link
            to={`/${eventslug}/event`}
            className="btn btn-outline-secondary"
          >
            Event Home
          </Link>
        </div>
      </div>
    </section>
  </>
  );
};

export default StoreSuccess;